<template>
  <div class="dashboard">
    <div class="dashboard-header">
      <h2 class="title is-4">Welcome back, {{ user.fullName || "Admin" }}!</h2>
      <p class="subtitle is-6">
        Here's what's happening with your system today
      </p>
    </div>

    <!-- Dashboard Tabs -->
    <div class="tabs is-boxed">
      <ul>
        <li :class="{ 'is-active': activeTab === 'overview' }">
          <a @click="activeTab = 'overview'">
            <span class="icon is-small">
              <i class="fas fa-chart-line"></i>
            </span>
            <span>Overview</span>
          </a>
        </li>
        <li :class="{ 'is-active': activeTab === 'financial' }">
          <a @click="activeTab = 'financial'">
            <span class="icon is-small">
              <i class="fas fa-dollar-sign"></i>
            </span>
            <span>Financial</span>
          </a>
        </li>
        <li :class="{ 'is-active': activeTab === 'map' }">
          <a @click="activeTab = 'map'">
            <span class="icon is-small">
              <i class="fas fa-map-marked-alt"></i>
            </span>
            <span>Map View</span>
          </a>
        </li>
      </ul>
    </div>

    <!-- Tab Content -->
    <transition name="tab-fade" mode="out-in">
      <OverviewTab
        v-if="activeTab === 'overview'"
        key="overview"
        :stats="stats"
        :is-loading-stats="isLoadingStats"
        :recent-users="recentUsers"
        :recent-companies="recentCompanies"
        :recent-projects="recentProjects"
        :recent-cameras="recentCameras"
        :overview-chart-data="overviewChartData"
        :camera-types-data="cameraTypesData"
        :camera-type-trends-data="cameraTypeTrendsData"
        :overview-chart-key="overviewChartKey"
        :camera-types-key="cameraTypesKey"
        :camera-type-trends-key="cameraTypeTrendsKey"
        :line-chart-options="lineChartOptions"
        :pie-chart-options="pieChartOptions"
        @open-user-modal="openUserModal"
        @open-company-modal="openCompanyModal"
        @open-project-modal="openProjectModal"
        @open-camera-modal="openCameraModal"
        @update-overview-chart="updateOverviewChart"
        @update-camera-type-trends="updateCameraTypeTrends"
      />

      <FinancialTab
        v-else-if="activeTab === 'financial'"
        key="financial"
        :monthly-revenue="monthlyRevenue"
        :active-contracts="activeContracts"
        :expiring-contracts="expiringContracts"
        :overdue-invoices="overdueInvoices"
        :pending-invoices="pendingInvoices"
        :financial-chart-data="financialChartData"
        :package-distribution-data="packageDistributionData"
        :financial-chart-key="financialChartKey"
        :package-chart-key="packageChartKey"
        :line-chart-options="lineChartOptions"
        :pie-chart-options="pieChartOptions"
        @update-financial-chart="updateFinancialChart"
        @update-package-chart="updatePackageChart"
      />

      <MapTab
        v-else-if="activeTab === 'map'"
        key="map"
        ref="mapTab"
        :cameras="cameras"
        @open-camera-modal="openCameraModal"
      />
    </transition>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { httpInstance as http } from "@/plugins/http";
import endpoints from "@/api/endpoints";
import OverviewTab from "./tabs/OverviewTab.vue";
import FinancialTab from "./tabs/FinancialTab.vue";
import MapTab from "./tabs/MapTab.vue";

export default {
  name: "Dashboard",
  components: {
    OverviewTab,
    FinancialTab,
    MapTab,
  },
  data() {
    return {
      activeTab: "overview",
      isLoadingStats: false,
      stats: [
        {
          id: 1,
          label: "Total Users",
          value: 0,
          icon: "fa-users",
          color: "primary",
          change: 12,
          changeClass: "is-success",
        },
        {
          id: 2,
          label: "Companies",
          value: 0,
          icon: "fa-building",
          color: "info",
          change: 8,
          changeClass: "is-success",
        },
        {
          id: 3,
          label: "Projects",
          value: 0,
          icon: "fa-project-diagram",
          color: "success",
          change: -3,
          changeClass: "is-danger",
        },
        {
          id: 4,
          label: "Cameras",
          value: 0,
          icon: "fa-video",
          color: "warning",
          change: 15,
          changeClass: "is-success",
        },
        {
          id: 5,
          label: "Contracts",
          value: 0,
          icon: "fa-file-contract",
          color: "danger",
          change: 5,
          changeClass: "is-success",
        },
      ],
      dashboardData: null,
      // Financial stats
      expiringContracts: 0,
      overdueInvoices: 0,
      pendingInvoices: 0,
      monthlyRevenue: 0,
      activeContracts: 0,
      // Charts
      overviewChartData: null,
      cameraTypesData: null,
      cameraTypeTrendsData: null,
      packageDistributionData: null,
      overviewChartKey: 0,
      cameraTypesKey: 0,
      cameraTypeTrendsKey: 0,
      financialChartKey: 0,
      packageChartKey: 0,
      overviewPeriod: "1month",
      overviewCustomStart: null,
      overviewCustomEnd: null,
      cameraTypePeriod: "1month",
      cameraTypeCustomStart: null,
      cameraTypeCustomEnd: null,
      financialPeriod: "1month",
      financialCustomStart: null,
      financialCustomEnd: null,
      financialChartData: null,
      packageMetric: "quantity",
      lineChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "top",
          },
          tooltip: {
            mode: "index",
            intersect: false,
          },
        },
        scales: {
          y: {
            beginAtZero: true,
          },
        },
      },
      pieChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "right",
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                let label = context.label || "";
                if (label) {
                  label += ": ";
                }
                if (context.parsed !== null) {
                  label += context.parsed.toLocaleString();
                }
                return label;
              },
            },
          },
        },
      },
    };
  },
  computed: {
    ...mapState([
      "users",
      "companies",
      "projects",
      "cameras",
      "contracts",
      "user",
    ]),
    recentUsers() {
      return this.users.slice(0, 5);
    },
    recentCompanies() {
      return this.companies.slice(0, 5);
    },
    recentProjects() {
      return this.projects.slice(0, 5);
    },
    recentCameras() {
      return this.cameras.slice(0, 5);
    },
  },
  methods: {
    ...mapActions([
      "openUserModal",
      "openCompanyModal",
      "openProjectModal",
      "openCameraModal",
      "openContractModal",
    ]),
    calculateFinancialStats() {
      const now = new Date();
      const thirtyDaysFromNow = new Date(
        now.getTime() + 30 * 24 * 60 * 60 * 1000
      );
      this.expiringContracts = this.contracts.filter((c) => {
        if (!c.end_date) return false;
        const endDate = new Date(c.end_date);
        return endDate >= now && endDate <= thirtyDaysFromNow && !c.is_canceled;
      }).length;

      this.activeContracts = this.contracts.filter((c) => {
        if (c.is_canceled) return false;
        if (!c.end_date) return true;
        const endDate = new Date(c.end_date);
        return endDate >= now;
      }).length;

      this.overdueInvoices = Math.floor(Math.random() * 5) + 1;
      this.pendingInvoices = Math.floor(Math.random() * 10) + 3;
      this.monthlyRevenue = (Math.random() * 50000 + 10000).toFixed(0);
    },
    async fetchDashboardData() {
      this.isLoadingStats = true;
      try {
        const response = await http.get(endpoints.DASHBOARD_STATS, {
          skipAuthRedirect: true,
        });
        if (response.status === 200 && response.data) {
          this.updateStatsFromAPI(response.data);
        } else {
          this.loadMockStats();
        }
      } catch (error) {
        this.loadMockStats();
      } finally {
        this.isLoadingStats = false;
      }

      // Fetch all chart data - each method handles its own fallback to mock data
      await this.fetchGrowthTrends();
      await this.fetchCameraTypes();
      await this.fetchCameraTypeTrends();
      await this.fetchRevenue();
      await this.fetchFinancialStats();
      await this.fetchPackages();
    },
    updateStatsFromAPI(data) {
      this.stats[0].value = data.total_users || this.users.length;
      this.stats[1].value = data.total_companies || this.companies.length;
      this.stats[2].value = data.total_projects || this.projects.length;
      this.stats[3].value = data.total_cameras || this.cameras.length;
      this.stats[4].value = data.total_contracts || this.contracts.length;
    },
    loadMockStats() {
      this.stats[0].value = this.users.length;
      this.stats[1].value = this.companies.length;
      this.stats[2].value = this.projects.length;
      this.stats[3].value = this.cameras.length;
      this.stats[4].value = this.contracts.length;

      this.calculateFinancialStats();
    },
    async fetchGrowthTrends() {
      try {
        let url = endpoints.DASHBOARD_GROWTH_TRENDS;
        const params = [];

        if (this.overviewPeriod === "custom") {
          if (this.overviewCustomStart && this.overviewCustomEnd) {
            const start = new Date(this.overviewCustomStart).toISOString();
            const end = new Date(this.overviewCustomEnd).toISOString();
            params.push(`start=${encodeURIComponent(start)}`);
            params.push(`end=${encodeURIComponent(end)}`);
          }
        } else {
          params.push(`period=${this.overviewPeriod}`);
        }

        if (params.length > 0) {
          url += `?${params.join("&")}`;
        }

        const response = await http.get(url, { skipAuthRedirect: true });
        if (response.status === 200 && response.data) {
          this.overviewChartData = {
            labels: response.data.labels,
            datasets: [
              {
                label: "Users",
                data: response.data.users,
                borderColor: "#3273dc",
                backgroundColor: "rgba(50, 115, 220, 0.1)",
                tension: 0.4,
              },
              {
                label: "Companies",
                data: response.data.companies,
                borderColor: "#209cee",
                backgroundColor: "rgba(32, 156, 238, 0.1)",
                tension: 0.4,
              },
              {
                label: "Projects",
                data: response.data.projects,
                borderColor: "#48c774",
                backgroundColor: "rgba(72, 199, 116, 0.1)",
                tension: 0.4,
              },
              {
                label: "Cameras",
                data: response.data.cameras,
                borderColor: "#ffdd57",
                backgroundColor: "rgba(255, 221, 87, 0.1)",
                tension: 0.4,
              },
            ],
          };
          this.overviewChartKey++;
        }
      } catch (error) {
        // Leave data as null to show N/A
      }
    },
    async fetchCameraTypes() {
      try {
        const response = await http.get(endpoints.DASHBOARD_CAMERA_TYPES, {
          skipAuthRedirect: true,
        });
        if (response.status === 200 && response.data) {
          this.cameraTypesData = {
            labels: response.data.labels,
            datasets: [
              {
                data: response.data.data,
                backgroundColor: [
                  "#3273dc",
                  "#209cee",
                  "#48c774",
                  "#ffdd57",
                  "#f14668",
                  "#b5b5b5",
                ],
              },
            ],
          };
          this.cameraTypesKey++;
        }
      } catch (error) {
        // Keep mock data if API fails
      }
    },
    async fetchCameraTypeTrends() {
      try {
        let url = endpoints.DASHBOARD_CAMERA_TYPE_TRENDS;
        const params = [];

        if (this.cameraTypePeriod === "custom") {
          if (this.cameraTypeCustomStart && this.cameraTypeCustomEnd) {
            const start = new Date(this.cameraTypeCustomStart).toISOString();
            const end = new Date(this.cameraTypeCustomEnd).toISOString();
            params.push(`start=${encodeURIComponent(start)}`);
            params.push(`end=${encodeURIComponent(end)}`);
          }
        } else {
          params.push(`period=${this.cameraTypePeriod}`);
        }

        if (params.length > 0) {
          url += `?${params.join("&")}`;
        }

        const response = await http.get(url, { skipAuthRedirect: true });
        if (response.status === 200 && response.data) {
          this.cameraTypeTrendsData = {
            labels: response.data.labels,
            datasets: [
              {
                label: "Onvif",
                data: response.data.onvif,
                borderColor: "#3273dc",
                backgroundColor: "rgba(50, 115, 220, 0.1)",
                tension: 0.4,
              },
              {
                label: "Natilon Pro",
                data: response.data.natilon,
                borderColor: "#209cee",
                backgroundColor: "rgba(32, 156, 238, 0.1)",
                tension: 0.4,
              },
              {
                label: "Hikvision",
                data: response.data.hikvision,
                borderColor: "#48c774",
                backgroundColor: "rgba(72, 199, 116, 0.1)",
                tension: 0.4,
              },
              {
                label: "Dahua",
                data: response.data.dahua,
                borderColor: "#ffdd57",
                backgroundColor: "rgba(255, 221, 87, 0.1)",
                tension: 0.4,
              },
              {
                label: "Uniview",
                data: response.data.uniview,
                borderColor: "#f14668",
                backgroundColor: "rgba(241, 70, 104, 0.1)",
                tension: 0.4,
              },
            ],
          };
          this.cameraTypeTrendsKey++;
        }
      } catch (error) {
        // Leave data as null to show N/A
      }
    },
    async fetchRevenue() {
      try {
        let url = endpoints.DASHBOARD_REVENUE;
        const params = [];

        if (this.financialPeriod === "custom") {
          if (this.financialCustomStart && this.financialCustomEnd) {
            const start = new Date(this.financialCustomStart).toISOString();
            const end = new Date(this.financialCustomEnd).toISOString();
            params.push(`start=${encodeURIComponent(start)}`);
            params.push(`end=${encodeURIComponent(end)}`);
          }
        } else {
          params.push(`period=${this.financialPeriod}`);
        }

        if (params.length > 0) {
          url += `?${params.join("&")}`;
        }

        const response = await http.get(url, { skipAuthRedirect: true });
        if (response.status === 200 && response.data) {
          this.financialChartData = {
            labels: response.data.labels,
            datasets: [
              {
                label: "Revenue",
                data: response.data.data,
                borderColor: "#48c774",
                backgroundColor: "rgba(72, 199, 116, 0.1)",
                tension: 0.4,
              },
            ],
          };
          this.financialChartKey++;
        }
      } catch (error) {
        // Leave data as null to show N/A
      }
    },
    async fetchFinancialStats() {
      try {
        const url = `${endpoints.DASHBOARD_FINANCIAL_STATS}`;
        const response = await http.get(url, { skipAuthRedirect: true });
        if (response.status === 200 && response.data) {
          this.activeContracts = response.data.active_contracts;
          this.expiringContracts = response.data.expiring_contracts;
          this.overdueInvoices = response.data.overdue_invoices;
          this.pendingInvoices = response.data.pending_invoices;
          this.monthlyRevenue = response.data.monthly_revenue;
        }
      } catch (error) {
        // Leave data as null to show N/A
      }
    },
    async fetchPackages() {
      try {
        const url = `${endpoints.DASHBOARD_PACKAGES}?metric=${this.packageMetric}`;
        const response = await http.get(url, { skipAuthRedirect: true });
        if (response.status === 200 && response.data) {
          this.packageDistributionData = {
            labels: response.data.labels,
            datasets: [
              {
                data: response.data.data,
                backgroundColor: ["#209cee", "#48c774", "#f14668"],
              },
            ],
          };
          this.packageChartKey++;
        }
      } catch (error) {
        // Leave data as null to show N/A
      }
    },
    async updateOverviewChart() {
      await this.fetchGrowthTrends();
    },
    async updateFinancialChart() {
      await this.fetchRevenue();
    },
    async updatePackageChart(metric) {
      this.packageMetric = metric;
      await this.fetchPackages();
    },
    async updateCameraTypeTrends() {
      await this.fetchCameraTypeTrends();
    },
  },
  mounted() {
    this.fetchDashboardData();
  },
  watch: {
    contracts() {
      this.calculateFinancialStats();
    },
    activeTab(newTab) {
      if (newTab === "map" && this.$refs.mapTab) {
        this.$nextTick(() => {
          this.$refs.mapTab.invalidateSize();
        });
      }
    },
  },
};
</script>

<style scoped>
.dashboard {
  padding: 0;
}

.dashboard-header {
  margin-bottom: 2rem;
}

.tab-fade-enter-active,
.tab-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.tab-fade-enter-from {
  opacity: 0;
  transform: translateX(10px);
}

.tab-fade-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

.tab-fade-enter-to,
.tab-fade-leave-from {
  opacity: 1;
  transform: translateX(0);
}
</style>
